import React from 'react';
import { ApiCalls, emptyPettyCashResponseData, emptyResponseData, IBankTransactionData, IBankTransactionsResponseData, IPettyCashIndexData, IPettyCashResponseData, IReceiptData, IReceiptsResponseData, IUsersPettyCashResponseData } from '../../ApiCalls';
import { GlobalConstants } from '../../GlobalConstants';
import { useSelector } from 'react-redux';
import { selectedUserCurrencyState, selectedUserState } from '../../redux/slices/userSlice';
import { selectedYachtState } from '../../redux/slices/yachtSlice';
import { GlobalStyles } from '../../GlobalStyles';
import ConfirmAndNextLockedForm from './elements/ConfirmAndNextLockedForm';
import ConfirmAndNextUnmatchedForm from './elements/ConfirmAndNextUnmatchedForm';
import ConfirmAndNextPettyCashForm from './elements/ConfirmAndNextPettyCashForm';
import { GlobalVariables } from '../../GlobalVariables';
import { toast } from 'react-toastify';
import NavItem from './tooltiped/NavItem';
import SelectInput from './elements/Forms/SelectInput';
import { ReduxUpdater } from '../../redux/ReduxUpdater';
import { lastAuthenticatedUserState, lastConfirmedReceiptsState, lastCurrenciesState, lastExpensesState, lastImageState, lastPettyCashState, lastSelectedNavState, lastTransactionDateState, lastYachtCrewCardsState } from '../../redux/slices/tsSlice';
import Tooltip from './tooltiped/Tooltip';
import { documentLoadingErrorState, lastTransactionDateReloadingState, selectedNavPosState, selectedNavState, uploadedReceiptIdState, uploadReceiptFormState } from '../../redux/slices/statusesSlice';
import ECOM from './icons/ECOM';
import CreditCard from './icons/CreditCard';
import PettyCash from './icons/PettyCash';
import UploadingButton from './elements/UploadingButton';
import { ReceiptPreview } from './elements/ReceiptPreview';
import { receiptChangesState } from '../../redux/slices/receiptChangesSlice';
import ConfirmAndNextReimbursementForm from './elements/ConfirmAndNextReimbursementForm';
import ZohoDialog from './elements/ZohoDialog';
import CreateReceipt from './elements/CreateReceipt';
import { GlobalPermissions } from '../../GlobalPermissions';
import { Column, DataTable, DataTableDividers } from './elements/Forms/DataTable';
import { formatReceiptDate, formatStringDate } from './elements/Forms/DateTimeInput';
import AutoConfirm from './icons/AutoConfirm';
import SendBack from './icons/SendBack';
import { Button } from './ui/button';
import { Receipt, Regex } from 'lucide-react';
import PetyCashReceiptIcon from './icons/Receipt';
import ATM from './icons/ATM';
import ReceiptConfirmed from './icons/ReceiptConfirmed';
import ReceiptReported from './icons/ReceiptReported';
import Edit from './icons/Edit';
import View from './icons/View';
import { DualSelect, DualSelectTheme } from './elements/Forms/DualSelect';
import AddReceipt from './icons/AddReceipt';
import CopyToClipPastetoXLS from './icons/CopyToClipPastetoXLS';
import { ReceiptUpload } from './elements/ReceiptUpload';
import { IReceiptUploadFormData } from '../../redux/slices/receiptUploadSlice';
import { LocalStorage } from '../../LocalStorage';

export const receiptIcon = (receipt: IReceiptData, iconHeight = 16, extraStyles?: React.CSSProperties) => {
    if (receipt && Object.keys(receipt).length === 0) {
        return <></>;
    }
    return transactionIcon(receipt.matched_bank_transaction, iconHeight, extraStyles);
};

export const transactionIcon = (transaction: IBankTransactionData | null, iconHeight = 16, extraStyles?: React.CSSProperties) => {
    if (transaction !== null) {
        return transaction.extracted_type === 'ECOM'
            ? <ECOM height={iconHeight} {...extraStyles} />
            : <CreditCard height={iconHeight} {...extraStyles} />;
    }
    return <PettyCash height={iconHeight} {...extraStyles} />;
};

export const receiptType = (receipt: IReceiptData): string | null => {
    if (receipt && Object.keys(receipt).length === 0) {
        return null;
    }
    return transactionType(receipt.matched_bank_transaction);
};

export const transactionType = (transaction: IBankTransactionData | null): string | null => {
    var type = 'Boat Cash';
    if (transaction !== null) {
        type = transaction.extracted_type === 'ECOM'
            ? 'eCommerce'
            : 'Credit Card';
    }
    return `${type}`;
};

export const trimStringLength = (text: string | null, maxLen = 25) => {
    if (!text) {
        return null;
    }
    if (text.length < maxLen) {
        return text;
    }
    return (
        <Tooltip
            className={`items-center justify-center`}
            text={text}
            placement='top'
            textStyling='z-10 max-w-[350px] whitespace-break-spaces'
        >
            {text.slice(0, maxLen - 3)}...
        </Tooltip>
    );
}

const setSelectedNav = (newNav: number) => {
    ReduxUpdater.selectedNav = newNav;
}

const setSelectedNavPosition = (newNavPos: number) => {
    ReduxUpdater.selectedNavPos = newNavPos;
}

const ExpensesContent = () => {
    const authenticatedUserTS =  useSelector(lastAuthenticatedUserState);
    const currentSelectedYachtState = useSelector(selectedYachtState);
    const currentSelectedUserCurrencyState = useSelector(selectedUserCurrencyState);
    const currentSelectedUserState = useSelector(selectedUserState);
    const lastCurrenciesUpdatedTS = useSelector(lastCurrenciesState);
    const lastExpensesUpdatedTS = useSelector(lastExpensesState);
    const lastImageUpdatedTS = useSelector(lastImageState);
    const lastConfirmedReceiptsUpdatedTS = useSelector(lastConfirmedReceiptsState);
    const lastPettyCashUpdatedTS = useSelector(lastPettyCashState);
    const currentImageLoadingErrorState = useSelector(documentLoadingErrorState);
    const selectedNav = useSelector(selectedNavState);
    const selectedNavPosition = useSelector(selectedNavPosState);
    const lastSelectedNavTS = useSelector(lastSelectedNavState);
    const lastTransactionDateReloading = useSelector(lastTransactionDateReloadingState);
    const lastTransactionDateUpdatedTS = useSelector(lastTransactionDateState);
    const lastYachtCrewCardsTS = useSelector(lastYachtCrewCardsState);
    const receiptChanges = useSelector(receiptChangesState);
    const uploadReceiptForm = useSelector(uploadReceiptFormState);
    const uploadedReceiptID = useSelector(uploadedReceiptIdState);

    const [receipt, setReceipt] = React.useState({} as IReceiptData);
    const [isDataLoading, setIsDataLoading] = React.useState(false);

    var oldSelectedNav = -1;

    const [ImageURL, setImageURL] = React.useState('');
    const [SignedImageURL, setSignedImageURL] = React.useState('');
    const [documentType, setDocumentType] = React.useState<'image' | 'pdf'>('image');

    const [dataMissingReceipts, setDataMissingReceipts] = React.useState<IBankTransactionsResponseData>(emptyResponseData);
    const [MissingReceiptsPageOffset, setMissingReceiptsPageOffset] = React.useState(1);
    const [MissingReceiptsPageSize, setMissingReceiptsPageSize] = React.useState(LocalStorage.pageSizeMissingReceipts);

    const [dataReviewAndConfirmReceipts, setDataReviewAndConfirmReceipts] = React.useState<IReceiptsResponseData>(emptyResponseData);

    const [dataBoatCash, setDataBoatCash] = React.useState<IPettyCashResponseData>(emptyPettyCashResponseData);
    const [PettyCashPageOffset, setPettyCashPageOffset] = React.useState(1);
    const [PettyCashPageSize, setPettyCashPageSize] = React.useState(LocalStorage.pageSizeBoatCash);


    // const [dataAllCards, setDataAllCards] = React.useState(false);
    const [dataConfirmedReceipts, setDataConfirmedReceipts] = React.useState<IReceiptsResponseData>(emptyResponseData);
    const [ConfirmedReceiptsPageOffset, setConfirmedReceiptsPageOffset] = React.useState(1);
    const [ConfirmedReceiptsPageSize, setConfirmedReceiptsPageSize] = React.useState(LocalStorage.pageSizeConfirmedReceipts);

    const [dataUsersPettyCash, setUsersPettyCash] = React.useState<IUsersPettyCashResponseData[]>();

    const [highlighted, setHighlighted] = React.useState(-1);

    const [shouldFilterTransactions, setShouldFilterTransactions] = React.useState(false);
    const [userFilterId, setUserFilterId] = React.useState(-1);
    const [coOwnerFilter, setCoOwnerFilter] = React.useState('');
    const [filterByCoOwner, setFilterByCoOwner] = React.useState(false);
    const [sortByDate, setSortByDate] = React.useState(false);

    const [zohoUploading, setZohoUploading] = React.useState(false);
    const [zohoAccountCheck, setZohoAccountCheck] = React.useState(false);
    // const [uploadPreviousMonthOnly, setUploadPreviousMonthOnly] = React.useState(false);
    // const [limitReceiptToBeginigOfMonthOnly, setLimitReceiptToBeginigOfMonthOnly] = React.useState(false);

    const [doJumpToLast, setDoJumpToLast] = React.useState(true);

    React.useMemo(() => {
        LocalStorage.pageSizeBoatCash = PettyCashPageSize;
    }, [PettyCashPageSize]);

    React.useMemo(() => {
        LocalStorage.pageSizeConfirmedReceipts = ConfirmedReceiptsPageSize;
    }, [ConfirmedReceiptsPageSize]);

    React.useMemo(() => {
        LocalStorage.pageSizeMissingReceipts = MissingReceiptsPageSize;
    }, [MissingReceiptsPageSize]);

    React.useEffect(() => {
        console.log('Expenses.receiptChanges:', receiptChanges);
    }, [receiptChanges]);

    // Currencies
    React.useEffect(() => {
         ApiCalls.currencies().then(c => {
            GlobalVariables.currencies = c;
        }).catch(err => console.error(err));
    }, [lastExpensesUpdatedTS]);

    // zohoAuthCheck
    React.useEffect(() => {
        if (currentSelectedYachtState && selectedNav === 3) {
            ApiCalls.zohoAuthCheck(currentSelectedYachtState.id).then(b => setZohoAccountCheck(b)).catch(err => console.error(err));
        }
    }, [currentSelectedYachtState, selectedNav]);

    React.useEffect(() => {
        console.log('lastTransactionDateUpdatedTS', lastTransactionDateUpdatedTS);
        ReduxUpdater.lastTransactionDateReloading = true;
        ApiCalls.lastTransaction().then(d => {
            console.log('lastTransactionDateUpdatedTS ENDED!!!');
            ReduxUpdater.lastTransactionDateReloading = false;
            GlobalVariables.lastTransactionDate = d;
        }).catch(err => console.error(err));
    }, [lastTransactionDateUpdatedTS]);

    React.useMemo(() => {
        console.log('currentImageLoadingErrorState:', currentImageLoadingErrorState);
    }, [currentImageLoadingErrorState]);

    React.useMemo(() => {
        if (dataUsersPettyCash && dataUsersPettyCash.length > 0) {
            ReduxUpdater.selectedUserCurrency = dataUsersPettyCash[0].currency;
        }
    }, [dataUsersPettyCash]);
    
    const currentSelectedUserCurrencySymbol = React.useMemo(() => {
        console.log('currentSelectedUserCurrencySymbol:', currentSelectedUserCurrencyState);
        return GlobalVariables.currencySymbol(currentSelectedUserCurrencyState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedUserCurrencyState, lastCurrenciesUpdatedTS]);

    const dataUsersPettyCashItem = React.useMemo(() => {
        return dataUsersPettyCash?.find(o => o.currency === currentSelectedUserCurrencyState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedUserCurrencyState, dataUsersPettyCash, lastCurrenciesUpdatedTS]);

    React.useEffect(() => {
        if (ReduxUpdater.selectedNav !== selectedNav) {
            console.log('OwerWrite!!!', ReduxUpdater.selectedNav, selectedNav);
            ReduxUpdater.selectedNav = selectedNav;
        }
        if (selectedNav === 3 && oldSelectedNav !== selectedNav && ConfirmedReceiptsPageOffset !== 1) {
            setConfirmedReceiptsPageOffset(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNav]);

    React.useEffect(() => {
        ReduxUpdater.receiptChanges = {} as IReceiptData;
        setTimeout(() => ReduxUpdater.autoInitPreline(), 250);
    }, [selectedNavPosition]);

    // setDataMissingReceipts
    React.useEffect(() => {
        if (!currentSelectedYachtState || !currentSelectedUserState) {
            setDataMissingReceipts(emptyResponseData);
            return;
        }
        setIsDataLoading(true);
        ApiCalls.bankTransactionsUnmatched(currentSelectedYachtState.iban, currentSelectedUserState.card_name, MissingReceiptsPageOffset, MissingReceiptsPageSize).then(d => {
            setDataMissingReceipts({ ...d });
            setIsDataLoading(false);
        }).catch(err => console.error(err));
    }, [lastExpensesUpdatedTS, currentSelectedUserState, currentSelectedYachtState, MissingReceiptsPageOffset, MissingReceiptsPageSize]);

    // setDataPettyCash
    React.useEffect(() => {
        if (!currentSelectedYachtState || !currentSelectedUserState) {
            setDataBoatCash(emptyPettyCashResponseData);
            return;
        }
        ApiCalls.boatCash(currentSelectedUserState.id, currentSelectedUserCurrencyState, PettyCashPageOffset, PettyCashPageSize).then(d => setDataBoatCash({ ...d })).catch(err => console.error(err));
    }, [lastExpensesUpdatedTS, currentSelectedUserCurrencyState, currentSelectedUserState, currentSelectedYachtState, PettyCashPageOffset, PettyCashPageSize]);

    // setDataReviewAndConfirmReceipts
    React.useEffect(() => {
        if (uploadedReceiptID !== -1) {
            ReduxUpdater.reloadExpensesData();
            return;
        }
        ReduxUpdater.currentDialog = null;
    }, [uploadedReceiptID]);

    // setDataReviewAndConfirmReceipts
    React.useEffect(() => {
        if (!currentSelectedUserState) {
            setDataReviewAndConfirmReceipts(emptyResponseData);
            return;
        }
        ApiCalls.reviewAndConfirmReceipts([currentSelectedUserState.id], 1, 100).then(d => {
            setDataReviewAndConfirmReceipts({ ...d });
            if (uploadedReceiptID !== -1) {
                const item = d.items.map((o, i) => ({
                    id: o.id,
                    idx: i
                })).find(o => o.id === uploadedReceiptID);
                if (item) {
                    setSelectedNavPosition(item.idx);
                }
                ReduxUpdater.uploadedReceiptID = -1;
                return;
            }
            if (doJumpToLast) {
                setSelectedNavPosition((d.items.map((o, i) => ({
                    id: o.id,
                    idx: i
                })).reduce((prev, curr) => curr.id > prev.id ? curr : prev).idx ?? 0) + 1);
                setDoJumpToLast(false);
            }
        }).catch(err => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedUserState, lastExpensesUpdatedTS]);

    // accountingCategories, coOwners
    React.useEffect(() => {
        if (!currentSelectedYachtState) {
            GlobalVariables.accountingCategories = [];
            return;
        }
        ApiCalls.accountingCategories().then(d => { GlobalVariables.accountingCategories = [...d] }).catch(err => console.error(err));
        ApiCalls.coOwners(currentSelectedYachtState.id).then(d => { GlobalVariables.coOwners = d.items }).catch(err => console.error(err));
    }, [currentSelectedYachtState]);

    // setDataConfirmedReceipts
    React.useEffect(() => {
        const dataAllCards = [GlobalConstants.UserRole.ROOT, GlobalConstants.UserRole.ADMIN].includes(ReduxUpdater.authenticatedUser?.user_role ?? '');
        console.log('ApiCalls.reportedReceipts:', coOwnerFilter, currentSelectedUserState, dataAllCards, filterByCoOwner, ConfirmedReceiptsPageOffset, sortByDate, shouldFilterTransactions, userFilterId);

        if (!currentSelectedUserState) {
            setDataConfirmedReceipts(emptyResponseData);
            setConfirmedReceiptsPageOffset(-1);
            return;
        }
        console.log('ApiCalls.reportedReceipts:', ConfirmedReceiptsPageOffset, ReduxUpdater.yachtCrewCards);
        if (ConfirmedReceiptsPageOffset !== -1) {
            setIsDataLoading(true);
            ApiCalls.confirmedReceipts(
                dataAllCards
                    ? ReduxUpdater.yachtCrewCards.map(c => c.id)
                    : [currentSelectedUserState.id],
                ConfirmedReceiptsPageOffset,
                ConfirmedReceiptsPageSize,
                'desc',
                sortByDate ? 'transaction_date' : 'updated_at',
                shouldFilterTransactions ? (
                    filterByCoOwner ? `&co_owner_name=${coOwnerFilter}` :  `&confirmed_user_id=${userFilterId}`
                ) : ''
            ).then(d => {
                setDataConfirmedReceipts({ ...d });
                setIsDataLoading(false);
            }).catch(err => console.error(err));
        }
    }, [lastConfirmedReceiptsUpdatedTS, lastExpensesUpdatedTS, coOwnerFilter, currentSelectedUserState, filterByCoOwner, ConfirmedReceiptsPageOffset, ConfirmedReceiptsPageSize, sortByDate, shouldFilterTransactions, userFilterId]);

    React.useEffect(() => {
        if (!currentSelectedYachtState || !currentSelectedUserState) {
            setUsersPettyCash([]);
            return;
        }
        ApiCalls.userBoatCash(currentSelectedUserState.id).then(d => {
            setUsersPettyCash(d.filter(o => o.petty_cash !== 0.0));
        }).catch(err => console.error(err));
    }, [lastExpensesUpdatedTS, lastPettyCashUpdatedTS, currentSelectedUserState, currentSelectedYachtState]);

    React.useEffect(() => {
        console.log('setImageURL:', ImageURL);
        if (ImageURL === null || ImageURL === '') {
            ReduxUpdater.documentLoadingError = true;
            return;
        }
        console.log('ImageURL:', ImageURL);
        ReduxUpdater.documentLoading = true;
        ApiCalls.getSignedUrl(ImageURL).then(url => {
            console.log('setSignedImageURL:', url);
            setDocumentType(ImageURL.endsWith('.pdf') ? 'pdf' : 'image');
            setSignedImageURL(url);
            // ReduxUpdater.imageLoading = false;
        });
    }, [ImageURL]);

    React.useEffect(() => {
        const receipt = dataReviewAndConfirmReceipts.items[selectedNavPosition];
        if (selectedNavPosition > dataReviewAndConfirmReceipts.items.length - 1) {
            // Check if we are on the last pos then adjust current pos by -1
            ReduxUpdater.selectedNavPos = dataReviewAndConfirmReceipts.items.length > 0 ? dataReviewAndConfirmReceipts.items.length - 1 : 0;
        }
        if (receipt) {
            setReceipt({ ...receipt }); // Force refresh even if ReviewAndReportSelection doesnt change
            setImageURL(receipt.supabase_image_link);
        }
    }, [dataReviewAndConfirmReceipts, selectedNavPosition]);

    // const toggleSwitch = () => {
    //     setDataAllCards(!dataAllCards);
    // }

    /**
     * 
     * @param receipt 
     * @param onSuccess 
     * @param onError 
     * 
     * Will set receipt.status='trashed'
     * it laso needs to:
     *  - remove it from Review&Confirm data
     *  - remove it from Petty Cash data
     *  - update petty cash balance
     *  - leave R&C index unchanged and refresh data with next receipt
     *   - decrease R&C count by 1
     */
    const deletePettyCashReceiptHandler = (receipt: IReceiptData, onSuccess: () => void, onError: () => void) => {
        const saveReceiptChanges = {
            ...receiptChanges,
            id: receipt.id,
            message_id: receipt.message_id,
            receipt_status: 'trashed',
        } as IReceiptData;
        console.log('handleDelete.receiptChanges:', saveReceiptChanges);
        ApiCalls.updateReceipt(saveReceiptChanges).then(res => {
            console.log('ApiCalls.updateReceipt:', res, dataBoatCash);
            if (res !== null) {
                const loc = dataBoatCash.items.findIndex(o => o !== null && o !== undefined && o.id === saveReceiptChanges.message_id);
                if (loc !== -1) {
                    delete dataBoatCash.objects[saveReceiptChanges.message_id];
                    delete dataBoatCash.items[loc];
                    setDataBoatCash({ ...dataBoatCash });
                }

                dataReviewAndConfirmReceipts.items.splice(selectedNavPosition, 1);
                setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

                ReduxUpdater.reloadPettyCashBalance();
                ReduxUpdater.receiptChanges = {} as IReceiptData;
                onSuccess();
            } else {
                onError();
            }
        }).catch(err => {
            console.error(err);
            onError();
        });
    }

    const decNavPositionHandler = (onCancel?: () => void) => {
        const newPos = (dataReviewAndConfirmReceipts.items.length + selectedNavPosition - 1) % dataReviewAndConfirmReceipts.items.length;
        changeNavPositionHandler(newPos, onCancel);
    };

    const incNavPositionHandler = (onCancel?: () => void) => {
        const newPos =  (selectedNavPosition + 1) % dataReviewAndConfirmReceipts.items.length;
        changeNavPositionHandler(newPos, onCancel);
    };

    const changeNavPositionHandler = (newPos: number, onCancel?: () => void) => {
        console.log('changeNavPositionHandler:', newPos, Object.keys(receiptChanges), GlobalVariables.allowReceiptChanges, onCancel);
        ReduxUpdater.receiptChanges = {} as IReceiptData;
        setSelectedNavPosition(newPos);
    };

    const unconfirmReceiptHandler = React.useCallback((receipt: IReceiptData, onSuccess: () => void, onError: () => void, moveToPetty = true, moveToConfirmed = true, doEdit = false) => {
        console.log('unconfirmReceiptHandler', receipt);
        const receiptChanges = {
            id: receipt.id,
            message_id: receipt.message_id,
            receipt_status: null,
            confirmed_user_id: null,
        } as IReceiptData;
        ApiCalls.updateReceipt(receiptChanges).then(res => {
            if (res) {
                const pos = dataConfirmedReceipts.items.findIndex(o => o !== null && o.id === receipt.id);
                if (pos < 0) {
                    return onError();
                }
                
                if (moveToConfirmed || doEdit) {
                    dataReviewAndConfirmReceipts.items = [
                        receipt,
                        ...dataReviewAndConfirmReceipts.items
                    ];
                    ReduxUpdater.selectedNavPos = 0;
                    setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });
                    if (doEdit) {
                        ReduxUpdater.selectedNav = 2;
                    }
                }

                dataConfirmedReceipts.items.splice(pos, 1);
                setDataConfirmedReceipts({ ...dataConfirmedReceipts });

                if (moveToPetty && dataBoatCash.objects[receipt.message_id] !== undefined) {
                    (dataBoatCash.objects[receipt.message_id] as IReceiptData).receipt_status = null;
                    (dataBoatCash.objects[receipt.message_id] as IReceiptData).confirmed_user_id = null;
                    setDataBoatCash({ ...dataBoatCash });
                }

                if (dataConfirmedReceipts.items.filter(o => o).length === 0) {
                    // Reload pages when last one is setn back
                    ReduxUpdater.reloadConfirmedReceiptsData();
                    // ReduxUpdater.reloadExpensesData();
                }

                onSuccess();
            } else {
                onError();
            }
            ReduxUpdater.receiptChanges = {} as IReceiptData;
        }).catch(err => {
            console.error(err);
            ReduxUpdater.receiptChanges = {} as IReceiptData;
            onError();
        });
    }, [dataBoatCash, dataConfirmedReceipts, dataReviewAndConfirmReceipts]);

    const RenderReviewAndConfirmForm = React.useMemo(() => {
        if (!receipt || Object.keys(receipt).length === 0) {
            return <></>;
        }

        console.log('RenderReviewAndConfirmForm:', receiptChanges);

        const handleConfirmAndNext = () => {
            if (currentImageLoadingErrorState) {
                toast.error('Can not confirm receipt if image failed to load!');
                return;
            }

            const saveReceiptChanges = {
                ...receiptChanges,
                id: receipt.id,
                message_id: receipt.message_id,
                receipt_status: 'confirmed',
            } as IReceiptData;
            if (ReduxUpdater.authenticatedUser) {
                saveReceiptChanges.confirmed_user_id = ReduxUpdater.authenticatedUser.id;
            }
            ApiCalls.updateReceipt(saveReceiptChanges).then(res => {
                if (res) {
                    dataReviewAndConfirmReceipts.items.splice(selectedNavPosition, 1);
                    setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

                    if (dataBoatCash.objects[receipt.message_id] !== undefined) {
                        (dataBoatCash.objects[receipt.message_id] as IReceiptData).receipt_status = 'confirmed';
                        if (ReduxUpdater.authenticatedUser) {
                            (dataBoatCash.objects[receipt.message_id] as IReceiptData).confirmed_user_id = ReduxUpdater.authenticatedUser.id;
                        }
                        setDataBoatCash({ ...dataBoatCash });
                    }

                    toast.success('Receipt Confirmed');
                    ReduxUpdater.reloadConfirmedReceiptsData();
                } else {
                    toast.error('Error Confirming receipt!');
                }
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            }).catch(err => {
                console.error(err);
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            });
        }

        const handlePettyCashConfirmAndNext = () => {
            if (currentImageLoadingErrorState) {
                toast.error('Can not confirm receipt if image failed to load!');
                return;
            }

            const saveReceiptChanges = {
                ...receiptChanges,
                id: receipt.id,
                message_id: receipt.message_id,
                receipt_status: 'confirmed',
            } as IReceiptData;
            if (ReduxUpdater.authenticatedUser) {
                saveReceiptChanges.confirmed_user_id = ReduxUpdater.authenticatedUser.id;
            }
            ApiCalls.updateReceipt(saveReceiptChanges).then(res => {
                if (res) {
                    dataReviewAndConfirmReceipts.items.splice(selectedNavPosition, 1);
                    setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

                    if (dataBoatCash.objects[receipt.message_id] !== undefined) {
                        (dataBoatCash.objects[receipt.message_id] as IReceiptData).receipt_status = 'confirmed';
                        if (ReduxUpdater.authenticatedUser) {
                            (dataBoatCash.objects[receipt.message_id] as IReceiptData).confirmed_user_id = ReduxUpdater.authenticatedUser.id;
                        }
                        setDataBoatCash({ ...dataBoatCash });
                    }

                    toast.success('Receipt Confirmed');
                    ReduxUpdater.reloadConfirmedReceiptsData();
                } else {
                    toast.error('Error Confirming receipt!');
                }
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            }).catch(err => {
                console.error(err);
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            });
        }

        /**
         * Will set receipt.receipt_type = 'petty_cash'
         * 
         * It also needs to update data in:
         *   - add to PettyCash table
         *   - in Review & Confirm table
         *   - Update Petty$ balance!!!
         */
        const handleMarkAsPettyCash = () => {
            receipt.receipt_type = 'petty_cash';
            ApiCalls.updateReceipt(receipt).then(res => {
                if (res) {
                    dataBoatCash.objects[receipt.message_id] = receipt;
                    dataBoatCash.items.push({
                        id: receipt.message_id,
                        date: receipt.transaction_date,
                        type: 'CASH_RECEIPT'
                    } as IPettyCashIndexData);
                    setDataBoatCash({ ...dataBoatCash });

                    dataReviewAndConfirmReceipts.items[selectedNavPosition] = receipt;
                    setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

                    setReceipt({ ...receipt });
                    ReduxUpdater.reloadPettyCashBalance();

                    toast.success('Receipt Marked as Boat Cash');
                } else {
                    toast.error('Error Marking receipt as Boat Cash!');
                }
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            }).catch(err => {
                console.error(err);
                ReduxUpdater.receiptChanges = {} as IReceiptData;
            });
        }

        // const handleSaveAndNext = () => {
        //     if (Object.keys(receiptChanges).length > 0) {
        //         ApiCalls.updateReceipt(receiptChanges).then(res => {
        //             if (res) {
        //                 const modifiedData = {
        //                     ...receipt,
        //                     ...receiptChanges
        //                 };
        //                 dataReviewAndConfirmReceipts.items[selectedNavPosition] = modifiedData;
        //                 setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

        //                 if (dataPettyCash.objects[receipt.message_id] !== undefined) {
        //                     dataPettyCash.objects[receipt.message_id] = modifiedData;
        //                     setDataPettyCash({ ...dataPettyCash });
        //                 }

        //                 setSelectedNavPosition(selectedNavPosition + 1);
        //                 toast.success('Receipt Saved');
        //             } else {
        //                 toast.error('Error Saving receipt!');
        //             }
        //             ReduxUpdater.receiptChanges = {} as IReceiptData;
        //         }).catch(err => {
        //             console.error(err);
        //         });
        //     } else {
        //         setSelectedNavPosition(selectedNavPosition + 1);
        //     }
        // }

        const handleSave = () => {
            console.log('handleSave:', receiptChanges);
            if (Object.keys(receiptChanges).length > 0) {
                ReduxUpdater.formUpdating = true;
                ApiCalls.updateReceipt(receiptChanges).then(res => {
                    if (res) {
                        console.log('RES:', res);
                        const modifiedData = {
                            ...receipt,
                            ...res
                        };
                        dataReviewAndConfirmReceipts.items[selectedNavPosition] = modifiedData;
                        setDataReviewAndConfirmReceipts({ ...dataReviewAndConfirmReceipts });

                        if (dataBoatCash.objects[receipt.message_id] !== undefined) {
                            dataBoatCash.objects[receipt.message_id] = modifiedData;
                            setDataBoatCash({ ...dataBoatCash });
                        }

                        if (res.matched_bank_transaction_id !== null && res.matched_bank_transaction_id !== receipt.matched_bank_transaction_id) {
                            GlobalVariables.dialogConfig.textCancel = 'OK';
                            GlobalVariables.dialogConfig.textConfirm = '';
                            GlobalVariables.dialogConfig.title = 'Matched to transaction';
                            GlobalVariables.dialogConfig.content = 'After your changes, this receipt was matched to a bank transaction and no longer affects your cash balance.';

                            if (dataMissingReceipts.items.length > 0) {
                                const transaction = dataMissingReceipts.items.filter(o => o !== undefined && o !== null)
                                    .map((transaction, i) => ({
                                        id: transaction.id,
                                        idx: i,
                                    }))
                                    .find(o => o.id === res.matched_bank_transaction_id);
                                if (transaction) {
                                    dataMissingReceipts.items.splice(transaction.idx, 1);
                                    dataMissingReceipts.total = dataMissingReceipts.total - 1; // Update total items
                                    setDataMissingReceipts({ ...dataMissingReceipts });
                                }
                            }

                            ReduxUpdater.currentDialog = 'confirmAction';
                        } else {
                            toast.success('Receipt Saved');
                        }
                        ReduxUpdater.formUpdating = false;
                    } else {
                        toast.error('Error Saving receipt!');
                        ReduxUpdater.formUpdating = false;
                    }
                    ReduxUpdater.receiptChanges = {} as IReceiptData;
                }).catch(err => {
                    console.error(err);
                });
            }
        }

        const handleDeletePettyCash = () => {
            console.log('handleDeletePettyCash');
            deletePettyCashReceiptHandler(receipt, () => {
                ReduxUpdater.currentDialog = null;
                toast.success('Boat Cash Removed');
            }, () => {
                toast.error('Error Removing Boat Cash!');
            });
        }

        if (receipt.receipt_type === 'reimbursement') {
            return <ConfirmAndNextReimbursementForm
                idx={selectedNavPosition}
                max={dataReviewAndConfirmReceipts.items.length}
                receipt={receipt}
                receiptChanges={receiptChanges}
                onConfirm={handlePettyCashConfirmAndNext}
                onSave={handleSave}
                onPrev={() => decNavPositionHandler()}
                onNext={() => incNavPositionHandler()}
            />;
        }

        if (receipt.receipt_type === 'petty_cash') {
            return <ConfirmAndNextPettyCashForm
                idx={selectedNavPosition}
                max={dataReviewAndConfirmReceipts.items.length}
                receipt={receipt}
                receiptChanges={receiptChanges}
                onConfirm={handlePettyCashConfirmAndNext}
                onSave={handleSave}
                onPrev={() => decNavPositionHandler()}
                onNext={() => incNavPositionHandler()}
                onDelete={handleDeletePettyCash}
            />;
        }

        if (receipt.matched_bank_transaction !== null) {
            if (receipt.matched_bank_transaction.extracted_type === 'ATM') {
                return <ConfirmAndNextPettyCashForm
                    idx={selectedNavPosition}
                    max={dataReviewAndConfirmReceipts.items.length}
                    receipt={receipt}
                    receiptChanges={receiptChanges}
                    onConfirm={handlePettyCashConfirmAndNext}
                    onSave={handleSave}
                    onPrev={() => decNavPositionHandler()}
                    onNext={() => incNavPositionHandler()}
                    onDelete={handleDeletePettyCash}
                />;
            }
            
            return <ConfirmAndNextLockedForm
                idx={selectedNavPosition}
                max={dataReviewAndConfirmReceipts.items.length}
                receipt={receipt}
                receiptChanges={receiptChanges}
                onConfirm={handleConfirmAndNext}
                onSave={handleSave}
                onPrev={() => decNavPositionHandler()}
                onNext={() => incNavPositionHandler()}
            />;
        }

        if (receipt.transaction_date) {
            console.log('TIME:', receipt.transaction_date, GlobalVariables.lastTransactionDate);
        }
        if (receipt.transaction_date && new Date(receipt.transaction_date).getTime() < GlobalVariables.lastTransactionDate.getTime()) {
            return <ConfirmAndNextPettyCashForm
                idx={selectedNavPosition}
                max={dataReviewAndConfirmReceipts.items.length}
                receipt={receipt}
                receiptChanges={receiptChanges}
                onConfirm={handlePettyCashConfirmAndNext}
                onSave={handleSave}
                onPrev={() => decNavPositionHandler(handleSave)}
                onNext={() => incNavPositionHandler(handleSave)}
                onDelete={handleDeletePettyCash}
            />;
        }

        return <ConfirmAndNextUnmatchedForm
            idx={selectedNavPosition}
            max={dataReviewAndConfirmReceipts.items.length}
            receipt={receipt}
            onConfirm={() => incNavPositionHandler(handleSave)}
            onSave={handleSave}
            onMark={handleMarkAsPettyCash}
            onPrev={() => decNavPositionHandler(handleSave)}
            onNext={() => incNavPositionHandler(handleSave)}
        />;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticatedUserTS, lastCurrenciesUpdatedTS, lastTransactionDateReloading, dataMissingReceipts, dataBoatCash, dataReviewAndConfirmReceipts, receipt, receiptChanges, selectedNavPosition, uploadReceiptForm]);

    const MissingReceiptsTable = React.useMemo(() => {
        console.log('MissingReceiptsTable', Date.now());
        if (dataMissingReceipts.items.length === 0) {
            return (
                <div className="w-[1000px] self-stretch px-4 inline-flex justify-between items-start">
                    <div className="w-[1000px] p-16 text-xl bg-bg-banner-light border border-1 border-border-default rounded-lg inline-flex flex-col justify-start items-center overflow-hidden">
                        No missing receipts!
                    </div>
                </div>
            );
        }

        const MissingReceiptsCOLUMNS: Column<IBankTransactionData>[] = [
            {
                key: "type", label: "Type",
                width: "1fr",
                sortable: false,
                render: (_, row) => {
                    const amount = parseFloat(row.extracted_amount);
                    return (
                        <div className="whitespace-nowrap text-center text-sm font-semibold text-text-dark dark:text-text-inverted">
                            <Tooltip
                                className='help text-center'
                                text={`${transactionType(row)} ${amount < 0 ? 'refund' : 'transaction'}`}
                                placement="top-start"
                            >
                                {transactionIcon(row, 24, {margin: 'auto'})}
                            </Tooltip>
                        </div>
                    );
                },
            },
            {
                key: "transaction_date", label: "Date",
                width: "1fr",
                sortable: false,
                render: (_, row) => (<>{formatStringDate(row.extracted_date ?? row.booking_date)}</>),
            },
            {
                key: "partner_name", label: "Merchant",
                width: "2fr",
                sortable: false,
                render: (v) => (<>{trimStringLength(v as string, 30)}</>),
            },
            {
                key: "amount",
                label: "Amount",
                width: "1fr",
                align: "center",
                sortable: false,
                render: (_, row) => {
                    const amount = parseFloat(row.extracted_amount);
                    const sign = Math.sign(-amount);
                    return (
                        <span className={`p-1 rounded-lg ${amount < 0 ? 'border-blue-400 bg-blue-400 border-1 text-white' : ''}`}>
                            {sign < 0 ? '- ' : ''}{GlobalVariables.currencySymbol(row.extracted_currency ?? 'EUR')}{Math.abs(amount).toFixed(2)}
                        </span>
                    )
                },
            },
            {
                key: "extracted_location",
                label: "Location",
                width: "2fr",
                sortable: false,
                // render: (_, row) => (<>{row.maris_category_id !== null ? trimStringLength(GlobalVariables.accountingCategoriesByID[row.maris_category_id], 30) : ''}</>),
            },
            {
                key: "action",
                label: (<>
                    <button
                        // className='p-1 text-white bg-slate-400 rounded-md text-xs'
                        className="mr-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            const divider = '\t';
                            const data = dataMissingReceipts.items.map(o => `"${transactionType(o)} ${parseFloat(o.extracted_amount) < 0 ? 'refund' : 'transaction'}"${divider}"${formatStringDate(o.extracted_date ?? o.booking_date)}"${divider}"${o.partner_name}"${divider}"${o.extracted_currency}"${divider}"${o.extracted_amount}"${divider}"${o.extracted_location}"`).join('\r');
                            navigator.clipboard.writeText(`TYPE${divider}DATE${divider}MERCHANT${divider}CURRENCY${divider}AMOUNT${divider}LOCATION\r${data}`);
                            toast.success('Data copied to clipboard');
                        }}
                    >
                        <Tooltip
                            className="flex px-0"
                            text="Copy page data to clipboard - Paste to XLS"
                            placement="top-end"
                        >
                            <CopyToClipPastetoXLS height={16} width={16} />
                        </Tooltip>
                    </button>
                </>),
                width: "1fr",
                align: "center",
                render: (_, row) => {
                    const amount = parseFloat(row.extracted_amount);
                    const transactionDate = row.extracted_date ?? row.booking_date;
                    var limitDate = new Date().getTime() + 86400000;

                    if (!GlobalPermissions.UX.EXPENSES.MissingReceipt.AllowFakeReceiptsDates.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')) {
                        const d = new Date();
                        d.setDate(1);
                        limitDate = d.getTime();
                    }

                    return (
                        GlobalPermissions.UX.EXPENSES.MissingReceipt.CreateFakeReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')
                            && new Date(transactionDate).getTime() < limitDate
                            && Math.abs(parseFloat(row.partner_amount)) <= (GlobalPermissions.UX.EXPENSES.MissingReceipt.CreateFakeReceiptsUpTo[ReduxUpdater.authenticatedUser?.user_role ?? ''] ?? 0.0)
                            // && transaction.extracted_type === 'ECOM'
                        ? <Tooltip
                            text={`Create missing receipt for this ${amount < 0 ? 'refund' : 'transaction'}`}
                            placement="top-end"
                        >
                            <Button
                                className='p-0'
                                variant="ghost"
                                onClick={() => {
                                    GlobalVariables.manualReceipt = {
                                        amount: amount.toFixed(2),
                                        currency: row.extracted_currency ?? 'EUR',
                                        transaction_date: formatReceiptDate(new Date(transactionDate)),
                                        vendor_name: trimStringLength(row.partner_name),
                                        user_id: currentSelectedUserState?.id,
                                    } as IReceiptData;
                                    ReduxUpdater.reloadSelectedNav();
                                }}
                            >
                                {amount > 0
                                    ? <Receipt className='p-0 !h-6 !w-6' />
                                    : <Regex className='p-0 !h-6 !w-6' />
                                }
                            </Button>
                        </Tooltip>
                        : <td className={GlobalStyles.table.td}>&nbsp;</td>
                    );
                },
            },
        ];

        return (
            <div
                className={GlobalStyles.expenses.container}
                style={{
                    maxHeight: 'calc(100vh - 200px)'
                }}
            >
                <DataTable<IBankTransactionData>
                    className='h-full w-full mb-4 min-h-[400px]'
                    columns={MissingReceiptsCOLUMNS}
                    data={dataMissingReceipts.items}
                    pagination={{ page: MissingReceiptsPageOffset, pageSize: MissingReceiptsPageSize, total: dataMissingReceipts.total }}
                    loading={isDataLoading}
                    onPageChange={setMissingReceiptsPageOffset}
                    onPageSizeChange={setMissingReceiptsPageSize}
                    rowCss={'bg-[rgb(245, 245, 245)] h-12 min-h-12'}
                    styles={GlobalStyles.DataTable.table}
                />
            </div>
        );
    }, [currentSelectedUserState, dataMissingReceipts, isDataLoading, MissingReceiptsPageOffset, MissingReceiptsPageSize]);

    interface IBoatCashTableColumnData {
        [key: string]: unknown;
        id: string;
        date: string | null;
        amount: string;
        currency: string;
        description: string;
        merchant: string;
        status: string;
        type: string;
    }

    const statusIcon = (status: string) => {
        if (status === 'confirmed') {
            return <ReceiptConfirmed {...{margin: 'auto'}} />;    
        }
        if (status === 'locked') {
            return <ReceiptReported {...{margin: 'auto'}} />;    
        }
        return <PetyCashReceiptIcon {...{margin: 'auto'}} />;
    };

    const statusIconTooltip = (status: string) => {
        if (status === 'confirmed') {
            return <>Confirmed receipt<br/>(in Confirmed receipts tab)</>;
        }
        if (status === 'locked') {
            return <>Sent to accounting<br />(Locked)</>;
        }
        return <>Unconfirmed receipt<br/>(double-click to Review & Confirm)</>;
    };

    const BoatCashTable = React.useMemo(() => {
        const HighlightedValues: string[] = [];
        const DIVIDERS: DataTableDividers = {};
        const filteredItems = dataBoatCash.items.filter(o => o !== null && o !== undefined);
        var doPushMonthRow = filteredItems.length > 0 && new Date(filteredItems[0].date).getMonth() === GlobalVariables.currentMonth;

        const highlightedCSS = !doPushMonthRow && highlighted === 1 ? 'bg-blue-100' : undefined;

        const highlightedColor = (pos: number) => {
            if (highlightedCSS !== undefined) {
                return highlightedCSS;
            }
            if (highlighted === 3) {
                return 'outline-4 border-blue-400 bg-gray-300';
            }
            if (highlighted === pos) {
                return 'bg-blue-400 border-[transparent]';
            }
            return 'bg-slate-200';
        }

        const onEditPettyCashRow = (row: IBoatCashTableColumnData) => {
            setSelectedNavPosition(dataReviewAndConfirmReceipts.items.map((o, i) => ({
                id: o.message_id,
                idx: i
            })).find(o => o.id === row.id)?.idx ?? -1);
            setSelectedNav(2);
        };

        const FormatedData = dataBoatCash.items.flatMap((pettyCash, i) => {
            const itemMonth = new Date(pettyCash.date).getMonth();

            if (doPushMonthRow && i !== 0 && itemMonth !== GlobalVariables.currentMonth) {
                DIVIDERS[i] = {
                    content: (
                        <div className="self-stretch text-center p-1 mx-10 my-2.5 text-white text-sm w-full bg-[#A0AEC0] rounded-xl font-semibold font-['Inter']">{GlobalConstants.MONTH[GlobalVariables.currentMonth]}</div>
                    ),
                    classes: "self-stretch bg-[var(--color-border-default)]",
                    styles: {
                        display: 'flex',
                        gridTemplateColumns: '1fr 1fr 3fr 1fr 2fr 1.25fr',
                        borderBottom: '1px solid #edf0f5',
                        animationDelay: `${i * 0.03}s`,
                    },
                };
                doPushMonthRow = false;
            }

            if (doPushMonthRow) {
                HighlightedValues.push(pettyCash.id);
            }

            if (pettyCash.type === 'ATM') {
                const o = dataBoatCash.objects[pettyCash.id] as IBankTransactionData;
                const transactionAmount = parseFloat(o.extracted_amount);

                return {
                    id: o.reference_number,
                    amount: o.extracted_amount,
                    currency: o.extracted_currency,
                    date: o.extracted_date ?? o.booking_date,
                    merchant: o.partner_name,
                    description: `ATM Cash ${transactionAmount > 0 ? 'withdrawal' : 'refund'}`,
                    status: '',
                    type: pettyCash.type,
                } as IBoatCashTableColumnData;
            } 

            const o = dataBoatCash.objects[pettyCash.id] as IReceiptData;
            return {
                id: o.message_id,
                amount: o.amount,
                currency: o.currency,
                date: o.transaction_date,
                merchant: o.vendor_name,
                description: o.user_description,
                status: o.receipt_status,
                type: pettyCash.type,
            } as IBoatCashTableColumnData;
        });

        const handleClickUncofirmReceipt = (receipt: IReceiptData) => {
            GlobalVariables.dialogConfig.textCancel = 'NO';
            GlobalVariables.dialogConfig.textConfirm = 'YES';
            GlobalVariables.dialogConfig.title = 'Unconfirm receipt';
            GlobalVariables.dialogConfig.content = <>Are you sure you want to <b>Unconfirm</b> this receipt and jump to <b>Review & Confirm</b> for editing.<br/></>;
            GlobalVariables.dialogConfig.onCancel = () => {
                ReduxUpdater.currentDialog = null;                              
            };
            GlobalVariables.dialogConfig.onConfirm = () => {
                ReduxUpdater.currentDialog = null;
                unconfirmReceiptHandler(receipt, () => {
                    toast.success('Receipt Unconfirmed');
                }, () => {
                    toast.success('Error Unconfirming receipt!');
                },
                receipt.user_id === ReduxUpdater.selectedUser?.id,
                true,
                true);
            };
            setTimeout(() => {
                ReduxUpdater.currentDialog = 'confirmAction';
            }, 250);
        }

        const BoatCashCOLUMNS: Column<IBoatCashTableColumnData>[] = [
            {
                key: "cards", label: "-",
                width: "1fr",
                align: 'center',
                sortable: false,
                render: (_, row) => {
                    if (row.type === 'ATM') {
                        return <Tooltip className='help' text="ATM withdrawal" placement='top-start'><ATM {...{margin: 'auto'}} /></Tooltip>;
                    }
                    return <Tooltip className='help' text={statusIconTooltip(row.status)} placement='top-start'>{statusIcon(row.status)}</Tooltip>;
                },
            },
            {
                key: "date", label: "Date",
                width: "1fr",
                align: 'center',
                sortable: false,
                render: (_, row) => (<>{formatStringDate(row.date)}</>),
            },
            {
                key: "merchant", label: "Merchant",
                width: "3fr",
                align: 'center',
                sortable: false,
                render: (_, row) => (<>{trimStringLength(row.merchant, 30)}</>),
            },
            {
                key: "amount",
                label: "Amount",
                width: "1fr",
                align: "center",
                sortable: false,
                render: (_, row) => {
                    const transactionAmount = parseFloat(row.amount);
                    var calcClasses = '';
                    if (HighlightedValues.includes(row.id)) {
                        if (highlighted === 1 && row.type !== 'ATM') {
                            calcClasses = (Math.sign(transactionAmount) < 0 ? 'border-green-400 bg-green-400 border-1 text-white' : 'border-blue-400 bg-blue-400 border-1 text-white');
                        }
                        if (highlighted === 2 && row.type === 'ATM') {
                            calcClasses = (Math.sign(transactionAmount) < 0 ? 'border-green-400 bg-green-400 border-1 text-white' : 'border-blue-400 bg-blue-400 border-1 text-white');
                        }
                    }
                    return (
                        <span className={`p-1 rounded-lg ${calcClasses}`}>
                            {(Math.sign(transactionAmount) > 0 && row.type !== 'ATM' ) ? '- ' : ''}{GlobalVariables.currencySymbol(row.currency)}{Math.abs(transactionAmount).toFixed(2)}
                        </span>
                    );
                },
            },
            {
                key: "description",
                label: "Description",
                width: "2fr",
                align: 'center',
                sortable: false,
                render: (_, row) => (<>{trimStringLength(row.description, 30)}</>),
            },
            {
                key: "action",
                label: (<>
                    <button
                        // className='p-1 text-white bg-slate-400 rounded-md text-xs'
                        className="mr-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            const divider = '\t';
                            
                            console.log(
                                currentSelectedUserState?.initial_petty_cash_balance,
                                currentSelectedUserState && currentSelectedUserState.initial_petty_cash_balance && Object.keys(currentSelectedUserState.initial_petty_cash_balance).includes(currentSelectedUserCurrencyState),
                                currentSelectedUserCurrencyState,
                                currentSelectedUserState && currentSelectedUserState.initial_petty_cash_balance && Object.keys(currentSelectedUserState.initial_petty_cash_balance).includes(currentSelectedUserCurrencyState) ? currentSelectedUserState.initial_petty_cash_balance[currentSelectedUserCurrencyState] : '???'
                            );

                            const data = FormatedData.map(o => {
                                const amount = parseFloat(o.amount);
                                return `${o.type}${divider}"${formatStringDate(o.date)}"${divider}"${o.merchant}"${divider}"${o.currency}"${divider}"${amount > 0 && o.type !== 'ATM' ? '-' : ''}${Math.abs(amount)}"${divider}"${o.description}"`;
                            }).join('\r');
                            var extra = '';
                            if (currentSelectedUserState && currentSelectedUserState.initial_petty_cash_balance && Object.keys(currentSelectedUserState.initial_petty_cash_balance).includes(currentSelectedUserCurrencyState)) {
                               extra = `\rINITIAL_BALANCE${divider}${divider}${divider}"${currentSelectedUserCurrencyState}"${divider}"${currentSelectedUserState.initial_petty_cash_balance[currentSelectedUserCurrencyState]}"${divider}"Initial boat cash balance"`;
                            }
                            navigator.clipboard.writeText(`TYPE${divider}DATE${divider}MERCHANT${divider}CURRENCY${divider}AMOUNT${divider}DESCRIPTION\r${data}${extra}`);
                            toast.success('Data copied to clipboard');
                        }}
                    >
                        <Tooltip
                            className="flex px-0"
                            text="Copy page data to clipboard - Paste to XLS"
                            placement="top-end"
                        >
                            <CopyToClipPastetoXLS height={16} width={16} />
                        </Tooltip>
                    </button>
                    {(GlobalPermissions.UX.EXPENSES.BoatCash.CreateBoatCashReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')
                        || (
                            GlobalPermissions.UX.EXPENSES.BoatCash.CreateBoatCashReceiptsLimited.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')
                                && ReduxUpdater.authenticatedUser?.id === currentSelectedUserState?.id
                        ))
                        && <button
                            // className='p-1 text-white bg-slate-400 rounded-md text-xs'
                            onClick={() => {
                                GlobalVariables.manualReceipt = {
                                    currency: currentSelectedUserCurrencyState,
                                    user_id: currentSelectedUserState?.id,
                                } as IReceiptData;
                                setSelectedNav(10);
                            }}
                        >
                            <Tooltip
                                className="flex px-0"
                                text="Add Manual Receipt for Boat Cash used"
                                placement="top-end"
                            >
                                <AddReceipt height={16} width={16} />
                            </Tooltip>
                        </button>}
                </>),
                width: "1.25fr",
                align: "right",
                render: (_, row) => {
                    if (row.type === 'ATM') {
                        return undefined;
                    }
                    const canEdit = ['locked'].includes(row.status) ? false : true;
                    return (
                        <>
                            {canEdit
                                ? <button
                                    className="invisible group-hover:visible"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClickUncofirmReceipt(dataBoatCash.objects[row.id] as IReceiptData);
                                    }}
                                >
                                    <Edit {...{ height: '16px', width: '16px' }} />
                                </button>
                                : <button
                                    className="invisible group-hover:visible"
                                    onClick={() => ReduxUpdater.offCanvas = {...dataBoatCash.objects[row.id] as IReceiptData}}
                                >
                                    <View {...{ height: '16px', width: '16px' }} />
                                </button>
                            }
                        </>
                    );
                },
            },
        ];

        return (
            <div
                className={GlobalStyles.expenses.container}
                style={{
                    maxHeight: 'calc(100vh - 200px)'
                }}
            >
                { dataUsersPettyCash && dataUsersPettyCashItem &&
                    <div className="self-stretch h-32 py-1 mb-2 inline-flex justify-start items-center gap-2 overflow-hidden">
                        <div
                            className={`border-4 basis-0 grow h-[78px] min-h-px min-w-px relative rounded-[8px] shrink-0 transition-colors duration-200 ${highlightedColor(0)}`}
                            onMouseEnter={() => setHighlighted(0)}
                            onMouseLeave={() => setHighlighted(-1)}
                        >
                            <div className="self-stretch flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[16px] py-[8px] relative w-full">
                                    <div className="content-stretch flex flex-col gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap w-[216px] whitespace-pre">
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[12px] text-black">Opening balance</p>
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[16px] text-black">{currentSelectedUserCurrencySymbol} {dataUsersPettyCashItem?.opening_balance_this_month.toFixed(2)}</p>
                                        <p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#4a5568] text-[9px]">Begining of month starting balance</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {highlighted === 0
                            ?
                            <div
                                className={`border-4 basis-0 grow h-[78px] min-h-px min-w-px relative rounded-[8px] shrink-0 transition-colors duration-200 outline-4 border-blue-400 bg-gray-300`}
                            >
                                <div className="flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                    <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[4px] py-[8px] relative w-full">
                                        <div className="self-stretch flex flex-col w-full gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap whitespace-pre">
                                            <div className="self-stretch inline-flex justify-between items-center">
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">Initial Balance</div>
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">{currentSelectedUserCurrencySymbol} {dataUsersPettyCashItem?.initial_balance.toFixed(2)}</div>
                                            </div>
                                            <div className="self-stretch inline-flex justify-between items-center">
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">Cash receipts before {GlobalConstants.MONTH[GlobalVariables.currentMonth].slice(0, 3)}</div>
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">-{currentSelectedUserCurrencySymbol}{dataUsersPettyCashItem?.cash_receipts_amount_before_month.toFixed(2)}</div>
                                            </div>
                                            <div className="self-stretch inline-flex justify-between items-center">
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">ATM withdrawals before {GlobalConstants.MONTH[GlobalVariables.currentMonth].slice(0, 3)}</div>
                                                <div className="justify-start text-text-lighter text-[10px] font-normal font-['Inter']">{currentSelectedUserCurrencySymbol}{(dataUsersPettyCashItem?.atm_withdrawals_amount_before_month ?? 0).toFixed(2)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            :
                            <div
                                className={`border-4 basis-0 grow h-[78px] min-h-px min-w-px relative rounded-[8px] shrink-0 transition-colors duration-200 ${highlightedColor(1)}`}
                                onMouseEnter={() => setHighlighted(1)}
                                onMouseLeave={() => setHighlighted(-1)}
                            >
                                <div className="flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                    <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[16px] py-[8px] relative w-full">
                                        <div className="content-stretch flex flex-col gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap w-[216px] whitespace-pre">
                                            <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[12px] text-black">Cash receipts this month</p>
                                            <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[16px] text-black">{currentSelectedUserCurrencySymbol} {/* dataUsersPettyCashItem?.cash_receipts_this_month !== 0 ? '-' : '' */}{dataUsersPettyCashItem?.cash_receipts_this_month.toFixed(2)}</p>
                                            <p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#4a5568] text-[9px]">Total of {dataUsersPettyCashItem?.cash_receipts_count_this_month} cash receipts this month</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                        <div
                            className={`border-4 basis-0 grow h-[78px] min-h-px min-w-px relative rounded-[8px] shrink-0 transition-colors duration-200 ${highlightedColor(2)}`}
                            onMouseEnter={() => setHighlighted(2)}
                            onMouseLeave={() => setHighlighted(-1)}
                        >
                            <div className="flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[16px] py-[8px] relative w-full">
                                    <div className="content-stretch flex flex-col gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap w-[216px] whitespace-pre">
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[12px] text-black">ATM withdrawals this month</p>
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[16px] text-black">{currentSelectedUserCurrencySymbol} {dataUsersPettyCashItem?.atm_withdrawals_this_month.toFixed(2)}</p>
                                        <p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#4a5568] text-[9px]">Total of {dataUsersPettyCashItem?.atm_withdrawals_count_this_month} ATM withdrawals this month</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`basis-0 grow h-[78px] min-h-px min-w-px relative rounded-[8px] shrink-0 transition-colors duration-200 ${highlighted === 3 ? 'bg-blue-400' : 'bg-slate-200'}`}
                            onMouseEnter={() => setHighlighted(3)}
                            onMouseLeave={() => setHighlighted(-1)}
                        >
                            <div className="flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[16px] py-[8px] relative w-full">
                                    <div className="content-stretch flex flex-col gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap w-[216px] whitespace-pre">
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[12px] text-black">Cash Balance</p>
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[16px] text-black">{currentSelectedUserCurrencySymbol} {dataUsersPettyCashItem?.petty_cash.toFixed(2)}</p>
                                        <p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#4a5568] text-[9px]">Total cash remaining in your wallet</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className={`basis-0 min-w-20 h-[78px] min-h-px relative rounded-[8px] shrink-0 transition-colors duration-200 bg-slate-200`}
                            onMouseEnter={() => setHighlighted(4)}
                            onMouseLeave={() => setHighlighted(-1)}
                        >
                            <div className="flex flex-col items-center overflow-clip rounded-[inherit] size-full">
                                <div className="box-border content-stretch flex flex-col h-[71px] items-center justify-between px-[16px] py-[8px] relative w-full">
                                    <div className="content-stretch flex flex-col gap-[7px] items-center justify-center leading-[normal] not-italic relative shrink-0 text-center text-nowrap w-[216px] whitespace-pre">
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[12px] text-black">Currency</p>
                                        <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[16px] text-black">
                                            {dataUsersPettyCash.length < 2
                                                ? currentSelectedUserCurrencyState
                                                : <select
                                                    id="hs-inline-leading-select-currency"
                                                    disabled={dataUsersPettyCash.length < 2}
                                                    name="hs-inline-leading-select-currency"
                                                    className="block w-full border-transparent rounded-lg focus:ring-blue-600 focus:border-blue-600 dark:text-neutral-500 dark:bg-neutral-900"
                                                    value={currentSelectedUserCurrencyState}
                                                    onChange={(e) => {
                                                        ReduxUpdater.selectedUserCurrency = e.currentTarget.value;
                                                    }}
                                                >
                                                    {/* {currentSelectedYachtCurrenciesState.map(c => <option value={c}>{c}</option>)} */}
                                                    {/* ToDO: linear GKZU-565  */}
                                                    {dataUsersPettyCash.map(c => <option value={c.currency}>{c.currency}</option>)}
                                                </select>
                                            }
                                        </p>
                                        <p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#4a5568] text-[8px]">{currentSelectedUserCurrencySymbol}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }

                {/* Table */}
                <DataTable<IBoatCashTableColumnData>
                    className='h-full w-full mb-4 min-h-[400px]'
                    columns={BoatCashCOLUMNS}
                    data={FormatedData}
                    dividers={DIVIDERS}
                    pagination={{ page: PettyCashPageOffset, pageSize: PettyCashPageSize, total: dataBoatCash.total }}
                    loading={isDataLoading}
                    onPageChange={setPettyCashPageOffset}
                    onPageSizeChange={setPettyCashPageSize}
                    onRowClick={(r) => {
                        if (r.type === 'ATM') {
                            return;
                        }
                        ReduxUpdater.offCanvas = {...dataBoatCash.objects[r.id] as IReceiptData};
                    }}
                    onRowDoubleClick={(r) => {
                        if (
                            r.type === 'ATM' ||
                            r.status === 'locked'
                        ) {
                            return;
                        }
                        if (r.status === 'confirmed') {
                            return;
                        }
                        onEditPettyCashRow(r);
                    }}
                    rowCss={(r, _) => {
                        if (r.type === 'ATM') {
                            return 'h-12 min-h-12 bg-input-status-atm';
                        }
                        if (r.status === 'locked') {
                            return 'h-12 min-h-12 bg-input-status-locked';    
                        }
                        if (r.status === 'confirmed') {
                            return 'h-12 min-h-12 bg-input-status-confirmed';    
                        }
                        return 'h-12 min-h-12 bg-input-edit-hover-over clickable';
                    }}
                    styles={GlobalStyles.DataTable.table}
                />
            </div>
        );
    }, [currentSelectedUserState, currentSelectedUserCurrencyState, currentSelectedUserCurrencySymbol, dataBoatCash, dataReviewAndConfirmReceipts, dataUsersPettyCash, dataUsersPettyCashItem, highlighted, isDataLoading, PettyCashPageOffset, PettyCashPageSize, unconfirmReceiptHandler]);

    const ReviewAndConfirmLayout = React.useMemo(() => {
        if (uploadReceiptForm) {
            return <ReceiptUpload/>;
        }

        if (dataReviewAndConfirmReceipts.items.length === 0) {
            return (
                <div className="w-[1000px] self-stretch px-4 inline-flex justify-between items-start">
                    <div className="w-[1000px] p-16 text-xl bg-bg-banner-light border border-1 border-border-default rounded-lg inline-flex flex-col justify-start items-center overflow-hidden">
                        {/* Nothing left to confirm! */}
                        <button
                            className="mt-2 p-2 bg-buttons-onState text-white rounded-xl"
                            onClick={() => {
                                ReduxUpdater.receiptUpload = { description: '', type: '-1', } as IReceiptUploadFormData;
                                ReduxUpdater.currentDialog = 'uploadReceipt';
                            }}
                        >
                            Upload receipt
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="w-[1000px] px-4 grid grid-cols-3 gap-4">
                <ReceiptPreview
                    className="col-span-2"
                    src={SignedImageURL !== '' ?  SignedImageURL : GlobalConstants.receiptPlaceholder}
                    alt={receipt.user_description ?? ''}
                    type={documentType}
                    description={trimStringLength(receipt.general_item_description, 75)}
                    allowDownload={ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT}
                    allowUpload={currentSelectedUserState?.user_role !== GlobalConstants.UserRole.ROOT}
                    cc={receipt.pan_number ?? ''}
                />
                    {RenderReviewAndConfirmForm}
            </div>
        );
    // ignore lastImageUpdatedTS unneeded
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedUserState, dataReviewAndConfirmReceipts, documentType, lastImageUpdatedTS, receipt, RenderReviewAndConfirmForm, SignedImageURL, uploadReceiptForm]);

    const ConfirmedReceiptsTable = React.useMemo(() => {
        const dataAllCards = [GlobalConstants.UserRole.ROOT, GlobalConstants.UserRole.ADMIN].includes(ReduxUpdater.authenticatedUser?.user_role ?? '');

        const zohoButtonDisabled = !zohoAccountCheck || currentSelectedYachtState === null || dataConfirmedReceipts.items.length === 0 || ReduxUpdater.authenticatedUser === null || ReduxUpdater.authenticatedUser.user_role !== GlobalConstants.UserRole.ROOT;

        const handleClickUncofirmReceipt = (receipt: IReceiptData) => {
            if (ReduxUpdater.authenticatedUser && receipt.user_id !== ReduxUpdater.authenticatedUser.id) {
                GlobalVariables.dialogConfig.textCancel = 'NO';
                GlobalVariables.dialogConfig.textConfirm = 'YES';
                GlobalVariables.dialogConfig.title = 'Unconfirm receipt';
                GlobalVariables.dialogConfig.content = <>Sending the receipt back to <b>Review & Confirm</b> tab.<br/><b>Jump to receipt edit?</b></>;
                GlobalVariables.dialogConfig.onCancel = () => {
                    ReduxUpdater.currentDialog = null;                              
                    unconfirmReceiptHandler(receipt, () => {
                        toast.success('Receipt Unconfirmed');
                    }, () => {
                        toast.success('Error Unconfirming receipt!');
                    },
                    receipt.user_id === ReduxUpdater.selectedUser?.id,
                    receipt.user_id === ReduxUpdater.selectedUser?.id);
                };
                GlobalVariables.dialogConfig.onConfirm = () => {
                    ReduxUpdater.currentDialog = null;
                    unconfirmReceiptHandler(receipt, () => {
                        toast.success('Receipt Unconfirmed');
                    }, () => {
                        toast.success('Error Unconfirming receipt!');
                    },
                    receipt.user_id === ReduxUpdater.selectedUser?.id,
                    true,
                    true);
                };
                setTimeout(() => {
                    ReduxUpdater.currentDialog = 'confirmAction';
                }, 250);
            } else {
                unconfirmReceiptHandler(receipt, () => {
                    toast.success('Receipt Unconfirmed');
                }, () => {
                    toast.success('Error Unconfirming receipt!');
                });
            }
        }

        const handleZohoUpload = (yachtId: number) => {
            setZohoUploading(true);
            GlobalVariables.dialogConfig.title = 'Uploading to Zoho';
            GlobalVariables.dialogConfig.content = <div className="block h-10 w-10 mx-auto my-4 rounded-full border-2 border-current border-r-transparent animate-spin align-[-0.125em] text-blue-400" role="status" aria-label="Loading"><span className="sr-only">Uploading...</span></div>;
            ReduxUpdater.currentDialog = 'waitProgress';
            ApiCalls.sendToZoho(yachtId, GlobalVariables.bPreviousMonthsOnly, GlobalVariables.bLimitPrecedingReceiptsToLastMonthStart).then(res => {
                if (res) {
                    GlobalVariables.dialogConfig.title = 'Upload complete';
                    GlobalVariables.dialogConfig.textConfirm = '';
                    GlobalVariables.dialogConfig.textCancel = 'Finish';

                    if (!res.success) {
                        GlobalVariables.dialogConfig.content = <><span className="text-xl text-red-600">{res.message}</span></>;
                    } else {
                        if (res.total_receipts === res.successful) {
                            GlobalVariables.dialogConfig.content = <span className="text-xl text-green-600">Successfully sent {res.successful}/{res.total_receipts} to Zoho</span>;
                        } else {
                            if (res.total_receipts === res.failed) {
                                GlobalVariables.dialogConfig.content = <><span className="text-xl text-red-600">Error sending {res.total_receipts} to Zoho:</span><ul className="text-xs text-red-600 list-inside list-disc">{res.errors.map(r => r.error).filter((value, index, self) => self.indexOf(value) === index).map(e => <li className="ml-5">{e}</li>)}</ul></>;
                            } else {
                                GlobalVariables.dialogConfig.content = <span className="text-xl text-yellow-700">Only {res.successful}/{res.total_receipts} were accepted by Zoho</span>;
                            }
                        }
                    }
                }
                console.log(res);
                setZohoUploading(false);
                ReduxUpdater.currentDialog = 'confirmAction';
                ReduxUpdater.reloadLastTransactionDate();
                ReduxUpdater.reloadExpensesData();
            }).catch(err => {
                console.error(err);
                setZohoUploading(false);
            });
        }

        const toggleFilterTransactionsSwitch = () => {
            if ([GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? '')) {
                return;
            }
            setShouldFilterTransactions(!shouldFilterTransactions);
            if (!shouldFilterTransactions) {
                setConfirmedReceiptsPageOffset(1);
            }
        }

        const toggleSortBySwitch = () => {
            setSortByDate(!sortByDate);
        }
    
        const ConfirmedReceiptsCOLUMNS: Column<IReceiptData>[] = [
            {
                key: "cards", label: "-",
                width: "1fr",
                sortable: false,
                render: (_, row) => (
                    <div className="whitespace-nowrap text-center text-sm font-semibold text-text-dark dark:text-text-inverted">
                        {dataAllCards && <span className="mr-1 inline-flex">{GlobalVariables.userIdToCC(row.user_id)}</span>}
                        <Tooltip
                            className='help inline-flex align-[-webkit-baseline-middle]'
                            text={`${row.confirmed_user_id === 0 ? 'Autoconfirmed ' : ''}${receiptType(row)} transaction`}
                            placement="top-start"
                        >
                            <div className="relative w-8 h-8 m-auto">
                                {receiptIcon(row, 22, {
                                    position: 'absolute',
                                })}
                                {row.confirmed_user_id === 0 && <AutoConfirm {...{
                                    position: 'absolute',
                                    transform: 'translate(8px, 8px)',
                                    scale: '0.85'
                                }} />}
                            </div>
                        </Tooltip>
                    </div>
                ),
            },
            {
                key: "transaction_date", label: "Date",
                width: "1fr",
                sortable: false,
                render: (_, row) => (<>{formatStringDate(row.transaction_date)}</>),
            },
            {
                key: "vendor_name", label: "Merchant",
                width: "2fr",
                sortable: false,
                render: (_, row) => (<>{trimStringLength(row.vendor_name, 30)}</>),
            },
            {
                key: "amount",
                label: "Amount",
                width: "1fr",
                align: "center",
                sortable: false,
                render: (_, row) => (<>{GlobalVariables.currencySymbol(row.currency)}{row.amount}</>),
            },
            {
                key: "category",
                label: "Category",
                width: "2fr",
                sortable: false,
                render: (_, row) => (<>{row.maris_category_id !== null ? trimStringLength(GlobalVariables.accountingCategoriesByID[row.maris_category_id], 30) : ''}</>),
            },
            {
                key: "action",
                label: (
                    GlobalPermissions.UX.EXPENSES.BoatCash.CreateBoatCashReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')
                        || (
                            GlobalPermissions.UX.EXPENSES.BoatCash.CreateBoatCashReceiptsLimited.includes(ReduxUpdater.authenticatedUser?.user_role ?? '')
                                && ReduxUpdater.authenticatedUser?.id === currentSelectedUserState?.id
                        )
                        ? <button
                            // className=""
                            onClick={(e) => {
                                e.stopPropagation();
                                const divider = '\t';
                                const data = dataConfirmedReceipts.items.map(o => `${GlobalVariables.userIdToCC(o.user_id)}${divider}"${formatStringDate(o.transaction_date)}"${divider}"${o.vendor_name}"${divider}"${o.currency}"${divider}"${o.amount}"${divider}"${o.maris_category_id !== null ? GlobalVariables.accountingCategoriesByID[o.maris_category_id] : ''}"`).join('\r');
                                navigator.clipboard.writeText(`CARD${divider}DATE${divider}MERCHANT${divider}CURRENCY${divider}AMOUNT${divider}CATEGORY\r${data}`);
                                toast.success('Data copied to clipboard');
                            }}
                        >
                            <Tooltip
                                className="flex px-4"
                                text="Copy page data to clipboard - Paste to XLS"
                                placement="top-end"
                            >
                                <CopyToClipPastetoXLS height={16} width={16} />
                            </Tooltip>
                        </button>
                        : '-'
                ),
                width: "1fr",
                align: "right",
                render: (_, row, i) => (
                    <>
                        {/* <button
                            className="invisible group-hover:visible"
                            onClick={() => ReduxUpdater.offCanvas = dataConfirmedReceipts.items[i]}
                        >
                            <View {...{ padding: '0px', margin: '0px', height: '16px', width: '16px' }} />
                        </button> */}
                        <button
                            className="hidden group-hover:visible group-hover:block"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClickUncofirmReceipt(row);
                            }}
                        >
                            <Tooltip
                                text="Return to Review&Confirm"
                                placement="top-end"
                            >
                                <SendBack {...{ height: '20px' }} />
                            </Tooltip>
                        </button>
                    </>
                ),
            },
        ];

        return (
            <div
                className={GlobalStyles.expenses.container}
                style={{
                    maxHeight: 'calc(100vh - 200px)'
                }}
            >
                {ReduxUpdater.authenticatedUser && <div className="self-stretch h-20 min-h-20 py-1 mb-2 inline-flex justify-start items-center gap-2 overflow-hidden">
                    <div className="flex-1 self-stretch p-4 bg-bg-header rounded-lg inline-flex flex-col justify-center items-center gap-4 overflow-hidden">
                        <div className="self-stretch flex flex-col justify-center items-start">
                            <div className="self-stretch inline-flex justify-start items-center gap-3">
                                <div className="flex-1 justify-start text-Text-Default-Default text-xs font-semibold font-['Inter']">Filter Transactions</div>
                                <label htmlFor="hs-basic-usage" className="relative inline-block w-6 h-3 cursor-pointer">
                                    <input
                                        id="switch-all-cards"
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={shouldFilterTransactions}
                                        disabled={[GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? '')}
                                    />
                                    <span
                                        className="border outline-1 border-black absolute inset-0 bg-gray-200 rounded-full transition-colors duration-200 ease-in-out peer-checked:bg-blue-600 dark:bg-neutral-700 dark:peer-checked:bg-blue-500 peer-disabled:opacity-50 peer-disabled:pointer-events-none"
                                        onClick={toggleFilterTransactionsSwitch}
                                    ></span>
                                    <span
                                        className="absolute top-1/2 start-0.5 -translate-y-1/2 size-2.5 bg-white rounded-full shadow-xs transition-transform duration-200 ease-in-out peer-checked:translate-x-full dark:bg-neutral-400 dark:peer-checked:bg-white"
                                        onClick={toggleFilterTransactionsSwitch}
                                    ></span>
                                </label>
                            </div>
                        </div>
                        <div className="self-stretch flex flex-col justify-center items-start">
                            <div className="self-stretch inline-flex justify-start items-center gap-3">
                                <div className="flex-1 justify-start text-Text-Default-Default text-xs font-semibold font-['Inter']">Sort By Transaction Date</div>
                                <label htmlFor="hs-basic-usage" className="relative inline-block w-6 h-3 cursor-pointer">
                                    <input
                                        id="switch-all-cards"
                                        type="checkbox"
                                        className="peer sr-only"
                                        checked={sortByDate}
                                    />
                                    <span
                                        className="border outline-1 border-black absolute inset-0 bg-gray-200 rounded-full transition-colors duration-200 ease-in-out peer-checked:bg-blue-600 dark:bg-neutral-700 dark:peer-checked:bg-blue-500 peer-disabled:opacity-50 peer-disabled:pointer-events-none"
                                        onClick={toggleSortBySwitch}
                                    ></span>
                                    <span
                                        className="absolute top-1/2 start-0.5 -translate-y-1/2 size-2.5 bg-white rounded-full shadow-xs transition-transform duration-200 ease-in-out peer-checked:translate-x-full dark:bg-neutral-400 dark:peer-checked:bg-white"
                                        onClick={toggleSortBySwitch}
                                    ></span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className={`flex-1 self-stretch px-5 py-[5px] bg-${shouldFilterTransactions ? 'buttons-onState' : 'bg-header'} rounded-lg flex justify-end items-center gap-2.5 overflow-hidden`}>
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
                            <div className="self-stretch inline-flex justify-between items-center">
                                <div className="flex">
                                    <input
                                        disabled={!shouldFilterTransactions}
                                        type="radio"
                                        name="hs-default-radio"
                                        className="shrink-0 mt-0.5 mr-1 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 checked:border-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                        checked={!filterByCoOwner}
                                        onClick={() => setFilterByCoOwner(false)}
                                    />
                                    <label data-for="hs-default-radio" className="justify-start text-black text-xs font-normal font-['Inter']">Confirmed By:</label>
                                </div>
                                <SelectInput
                                    overwriteClasses="w-36 h-5 px-[5px] bg-bg-input-locked rounded-lg outline outline-1 outline-offset-[-1px] outline-border-default flex justify-start items-center gap-2.5"
                                    selectClasses="justify-center bg-transparent text-Text-Default-Default text-xs font-normal font-['Inter']"
                                    idx="confirmedBy"
                                    type={shouldFilterTransactions ? 'edit' : 'locked'}
                                    label=""
                                    value={(ReduxUpdater.authenticatedUser?.id ?? -1).toString()}
                                    optionClasses='right-6'
                                    options={[
                                        {
                                            label: 'Myself',
                                            value: (ReduxUpdater.authenticatedUser?.id ?? -1).toString()
                                        },
                                        ...ReduxUpdater.yachtCrewCardsRest.map(c => ({
                                            label: c.first_name,
                                            value: c.id.toString()
                                        })),
                                        {
                                            label: 'Auto Confirmed',
                                            value: '0'
                                        },
                                    ]}
                                    onChange={(newUserId) => {
                                        if (parseInt(newUserId) !== userFilterId) {
                                            setConfirmedReceiptsPageOffset(1);
                                            setUserFilterId(parseInt(newUserId));
                                        }
                                    }}
                                />
                            </div>
                            <div className="self-stretch inline-flex justify-between items-center">
                                <div className="flex">
                                    <input
                                        disabled={!shouldFilterTransactions || GlobalVariables.coOwners.length < 1}
                                        type="radio"
                                        name="hs-default-radio"
                                        className="shrink-0 mt-0.5 mr-1 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 checked:border-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                        checked={filterByCoOwner}
                                        onClick={() => setFilterByCoOwner(true)}
                                    />
                                    <label data-for="hs-default-radio" className="justify-start text-black text-xs font-normal font-['Inter']">Co-Owner:</label>
                                </div>
                                <SelectInput
                                    overwriteClasses="w-36 h-5 px-[5px] bg-bg-input-locked rounded-lg outline outline-1 outline-offset-[-1px] outline-border-default flex justify-start items-center gap-2.5"
                                    selectClasses="justify-center bg-transparent text-Text-Default-Default text-xs font-normal font-['Inter']"
                                    idx="coOwner"
                                    type={shouldFilterTransactions && GlobalVariables.coOwnersArray.length > 1 ? 'edit' : 'locked'}
                                    label=""
                                    value={'Captain'}
                                    optionClasses='right-6'
                                    options={GlobalVariables.coOwnersArray}
                                    onChange={(newCoOwner) => {
                                        if (newCoOwner !== coOwnerFilter) {
                                            setConfirmedReceiptsPageOffset(1);
                                            setCoOwnerFilter(newCoOwner);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <UploadingButton
                        disabled={zohoButtonDisabled}
                        className={`clickable basis-0 grow self-stretch bg-buttons-${zohoButtonDisabled ? 'inactive' : 'primary'} min-h-px min-w-px relative rounded-[8px] shrink-0`}
                        label='Upload to Zoho'
                        uploadingLabel='Uploading'
                        uploading={zohoUploading}
                        onClick={() => {
                            console.log('Upload to Zoho:', zohoUploading, currentSelectedYachtState);
                            if (currentSelectedYachtState && !zohoUploading) {
                                GlobalVariables.dialogConfig.textCancel = 'Cancel';
                                GlobalVariables.dialogConfig.textConfirm = 'Continue';
                                GlobalVariables.dialogConfig.title = 'Upload to Zoho';
                                GlobalVariables.dialogConfig.content = <ZohoDialog />
                                GlobalVariables.dialogConfig.onConfirm = () => {
                                    handleZohoUpload(currentSelectedYachtState.id)
                                };
                                setTimeout(() => {
                                    ReduxUpdater.currentDialog = 'confirmAction';
                                }, 250);
                            }
                        }}
                    />  
                </div>}

                {/* Table */}
                <DataTable<IReceiptData>
                    columns={ConfirmedReceiptsCOLUMNS}
                    data={dataConfirmedReceipts.items}
                    pagination={{ page: ConfirmedReceiptsPageOffset, pageSize: ConfirmedReceiptsPageSize, total: dataConfirmedReceipts.total }}
                    loading={isDataLoading}
                    onPageChange={setConfirmedReceiptsPageOffset}
                    onPageSizeChange={setConfirmedReceiptsPageSize}
                    className='h-full w-full mb-4 min-h-[400px]'
                    rowCss={'h-12 min-h-12'}
                    styles={GlobalStyles.DataTable.table}
                    onRowClick={(row) => ReduxUpdater.offCanvas = {...row}}
                    onRowDoubleClick={(row) => handleClickUncofirmReceipt(row)}
                />
                {/* End Table */}
            </div>
        );
    }, [currentSelectedUserState, isDataLoading, coOwnerFilter, currentSelectedYachtState, dataConfirmedReceipts, filterByCoOwner, shouldFilterTransactions, sortByDate, userFilterId, zohoAccountCheck, zohoUploading, ConfirmedReceiptsPageOffset, ConfirmedReceiptsPageSize, unconfirmReceiptHandler]);

    const RenderNavContentElement = React.useMemo(() => {
        console.log('RenderNavContentElement', selectedNav, lastSelectedNavTS);
        switch (selectedNav) {
            case 1: return BoatCashTable;
            case 2: return ReviewAndConfirmLayout;
            case 3: return ConfirmedReceiptsTable;
            case 10: return (
                    <>
                        {/* <div className="w-[1000px] self-stretch mt-2 relative inline-flex flex-col justify-start items-start">
                            <div className="w-[1000px] px-4 flex flex-col justify-center items-start gap-6">
                                <div className="self-stretch inline-flex justify-start items-center gap-2">
                                    <div className="w-64 h-12 relative">
                                        <div className="w-64 left-0 top-0 absolute justify-start text-Text-Default-Default text-2xl font-semibold font-['Inter'] leading-7">Expenses</div>
                                        <div className="w-44 left-0 top-[33px] absolute justify-start text-text-lighter text-base font-normal font-['Inter'] leading-4">{currentSelectedYachtState?.name}</div>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                        <CreateReceipt
                            isBoatCash={true}
                            initData={GlobalVariables.manualReceipt ?? undefined}
                            showCancel={true}
                            onSuccess={() => {
                                ReduxUpdater.reloadExpensesData();
                                setSelectedNav(1);
                            }}
                        />
                    </>
                );
            default: return GlobalVariables.manualReceipt
            ? <CreateReceipt
                isBoatCash={false}
                initData={GlobalVariables.manualReceipt ?? undefined}
                showCancel={true}
                onSuccess={() => {
                    GlobalVariables.manualReceipt = null;
                    ReduxUpdater.reloadExpensesData();
                    ReduxUpdater.reloadSelectedNav();
                }}
            />
            : MissingReceiptsTable;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticatedUserTS, currentSelectedYachtState, lastSelectedNavTS, selectedNav, MissingReceiptsTable, BoatCashTable, ReviewAndConfirmLayout, ConfirmedReceiptsTable]);

    const cardOptions = React.useCallback((userID: number) => {
        const usersCards = ReduxUpdater.yachtCrew
            .filter(u => ReduxUpdater.authenticatedUser?.user_role !== GlobalConstants.UserRole.ROOT ? u.password !== 'disabled' : true)
            .filter(c => selectedNav < 1 ? GlobalVariables.userHasCard(c) || c.password === 'disabled' : true).sort(GlobalConstants.SORT.compareFnCrewCards).map((c, i) => ({
                label: `${c.id === userID ? 'My Expenses' : `${c.first_name} ${c.last_name[0]}.`}`,
                meta: GlobalVariables.userHasCard(c) ? `(${c.card_name}) ${c.debit_card_number && c.debit_card_number.length > 1 ? c.debit_card_number.slice(1) : c.debit_card_number}` : '',
                description: c.password === 'disabled' ? 'DISABLED' : c.position + ' / ' + c.user_role,
                value: c.id.toString(),
                sort: c.id === userID ? -1 : i,
                style: c.password === 'disabled' ? {
                    background: '#ff8080',
                    fontStyle: 'italic',
                } : undefined,
                styleDesc: c.password === 'disabled' ? {
                    color: 'whitesmoke',
                    // fontStyle: 'italic',
                } : undefined,
            }))
            .sort((a, b) => a.sort - b.sort);
        
        console.log('usersCards:', usersCards, currentSelectedUserState?.card_name);

        if (selectedNav < 3) {
            return usersCards;
        }

        return [
            {
                label: 'All Crew Expenses',
                value: 'ALL',
                sort: -1,
            }
        ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedUserState, lastYachtCrewCardsTS, selectedNav]);

    const renderCardSelector = React.useMemo(() => {
        if (ReduxUpdater.authenticatedUser === null) {
            return <></>;
        }

        if ([GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? '')) {
            if (selectedNav === 3) {
                return <>&nbsp;</>;
            }

            return (
                <div className="max-w-50 flex-1 flex justify-end items-center gap-2.5">
                    <div className="justify-start text-text-dark text-base font-semibold font-['Inter']">View:</div>
                    <div className="w-50 h-8 px-2.5 py-3 text-white bg-buttons-onState rounded-tl-lg rounded-tr-lg inline-flex justify-start items-center gap-2.5 overflow-hidden" >
                        <div className={`flex-1 py-1.5 sm:py-2 px-4 appearance-none focus:outline-none text-white bg-transparent'}`}>
                            My Expenses
                        </div>
                    </div>
                </div>
            );
        }

        const bgTrigger = selectedNav === 3 ? 'rgb(226 232 240)' : (currentSelectedUserState?.id === ReduxUpdater.authenticatedUser.id ? 'var(--color-button-onState)' : 'rgb(220 38 38)');
        const colTrigger = selectedNav === 3 ? 'black' : 'white';

        const ddTheme: Partial<DualSelectTheme> = {
            bg: {
                trigger:        bgTrigger,
                dropdown:       "white",
                optionHover:    "#f5f5f5",
                optionSelected: "#efefef",
            },
            text: {
                label:               "#999999",
                placeholder:         "#bbbbbb",
                triggerValue:        colTrigger,
                triggerMeta:         colTrigger,
                optionLabel:         "#333333",
                optionLabelSelected: "#000000",
                optionMeta:          "#999999",
                optionDesc:          "#bbbbbb",
                chevron:             colTrigger,
                selectedDot:         "#111111",
            },
            font: {
                family:       "'DM Mono', 'Fira Mono', 'Courier New', monospace",
                sizeLabel:    "11px",
                sizeTrigger:  "14px",
                sizeMeta:     "14px",
                sizeDesc:     "9px",
            },
            border: {
                trigger:         bgTrigger,
                triggerOpen:     "transparent",
                triggerFocusRing:"!none", //"rgba(0,0,0,0.08)",
                dropdown:        "#e0e0e0",
                divider:         "white",
            },
            dropdownShadow: "!none", //"0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)",
            height: {
                dropdown: "auto",
                trigger: "32px",
                divider: "1px",
            },
            radius: {
                dropdown: "10px",
                trigger: "10px 10px 0px 0px",
                option: "10px",
            }
        };

        return (
            <div className="max-w-64 flex flex-1 justify-end items-center gap-2.5">
                <DualSelect
                    options={cardOptions(ReduxUpdater.authenticatedUser.id)}
                    value={selectedNav === 3 ? 'ALL' : (currentSelectedUserState?.id.toString() ?? '')}
                    disabled={(selectedNav === 3 || [GlobalConstants.UserRole.STANDARD, GlobalConstants.UserRole.BASIC].includes(ReduxUpdater.authenticatedUser?.user_role ?? ''))}
                    onChange={(newID) => {
                        // if (newCard === 'ALL') {
                        //     setDataAllCards(true);
                        // } else {
                        //     setDataAllCards(false);
                        // }
                        if (parseInt(newID) !== currentSelectedUserState?.id && newID !== 'ALL') {
                            console.log('Card changed...', newID);
                            ReduxUpdater.selectedNavPos = 0;
                            ReduxUpdater.selectedUser = ReduxUpdater.yachtCrew.find(c => c.id === parseInt(newID)) ?? null;
                        }
                    }}
                    theme={ddTheme}
                />
            </div>
        );
    }, [cardOptions, currentSelectedUserState, selectedNav]);

    if (!currentSelectedYachtState) {
        return <>Please select Yacht first!!!</>;
    }

    return (
        <>
            <div className="w-[1000px] self-stretch mt-2 relative inline-flex flex-col justify-start items-start">
                <div className="w-[1000px] px-4 flex flex-col justify-center items-start gap-6">
                    <div className="self-stretch inline-flex justify-start items-center gap-2">
                        <div className="w-64 h-12 relative">
                            <div className="w-64 left-0 top-0 absolute justify-start text-Text-Default-Default text-2xl font-semibold font-['Inter'] leading-7">Expenses</div>
                            <div className="w-44 left-0 top-[33px] absolute justify-start text-text-lighter text-base font-normal font-['Inter'] leading-4">{currentSelectedYachtState.name}</div>
                        </div>
                        <div className="flex-1 flex justify-end items-center gap-2.5 self-stretch">&nbsp;</div>
                        <div className="max-w-50 flex-1 flex justify-end items-center gap-2.5">&nbsp;</div>
                    </div>
                    <div className="self-stretch border-0 inline-flex justify-start items-center gap-0">
                        {GlobalPermissions.UX.EXPENSES.ShowTabs.MissingReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '') && <NavItem
                            selected={selectedNav === 0}
                            disabled={
                                ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT
                                ? !GlobalVariables.userHasCard(currentSelectedUserState) && currentSelectedUserState?.password !== 'disabled'
                                : !GlobalVariables.userHasCard(currentSelectedUserState)
                            }
                            badge={dataMissingReceipts.total}
                            onClick={() => setSelectedNav(0)}
                            label="Missing Receipts"
                            tooltip="View bank transactions transactions that are still missing receipts|Not Available for selected user!"
                            placement="top-start"
                            tooltipCSS='|bg-red-400'
                        />}
                        {GlobalPermissions.UX.EXPENSES.ShowTabs.BoatCash.includes(ReduxUpdater.authenticatedUser?.user_role ?? '') && <NavItem
                            selected={selectedNav === 1}
                            // disabled={
                            //     ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT
                            //     ? !GlobalVariables.userHasCard(currentSelectedUserState) && currentSelectedUserState?.password !== 'disabled'
                            //     : !GlobalVariables.userHasCard(currentSelectedUserState)
                            // }
                            onClick={() => setSelectedNav(1)}
                            label="Boat Cash"
                            tooltip="View transactions affecting your Cash balance|Not Available for selected user!"
                            tooltipCSS='|bg-red-400'
                        />}
                        {GlobalPermissions.UX.EXPENSES.ShowTabs.ReviewAndConfirm.includes(ReduxUpdater.authenticatedUser?.user_role ?? '') &&<NavItem
                            selected={selectedNav === 2}
                            badge={dataReviewAndConfirmReceipts.items.length}
                            onClick={() => setSelectedNav(2)}
                            label="Review & Confirm"
                            tooltip="Review, Edit, Categorize and Report expense receipts"
                        />}
                        {GlobalPermissions.UX.EXPENSES.ShowTabs.ConfirmedReceipts.includes(ReduxUpdater.authenticatedUser?.user_role ?? '') && <NavItem
                            selected={selectedNav === 3}
                            onClick={() => setSelectedNav(3)}
                            label="Confirmed Receipts"
                            tooltip="Review reported expenses, Export Monthly reports"
                        />}
                        <div className="h-8 flex-1 py-1 flex justify-end items-center border-b-4 outline-b-4 border-border-default">
                            {selectedNav < 10 && renderCardSelector}
                        </div>
                    </div>
                </div>
            </div>
            {RenderNavContentElement}
        </>
    );
};

export default ExpensesContent;